import {
  AdControlNativeModel,
  AdControlNativeResponse,
  AdControlRequest,
  AdropParamsFactory,
  adcontrolQueryRequest,
} from '@/entities/advertisement';
import { AdPageType } from '@/models/ad/list';
import { Client } from '@/types/app';
import { QueryClient, useQuery } from '@tanstack/react-query';

const getKey = (props: AdControlRequest) => {
  return ['ad-control', 'native', props] as const;
};

const createNativeModel = (data: AdControlNativeResponse) => {
  return new AdControlNativeModel({
    response: data,
  });
};

const useAdControlNative = (
  props: AdControlRequest,
  options?: {
    enabled?: boolean;
    onError?: (error: Error) => void;
  }
) => {
  const enabled = options?.enabled ?? true;
  const {
    data: adModel,
    isInitialLoading: isLoading,
    error,
    isSuccess,
    refetch,
  } = useQuery(
    getKey(props),
    async () => {
      const response = await adcontrolQueryRequest({ ...props, adType: 'native' as const });
      return response.data as AdControlNativeResponse;
    },
    {
      enabled,
      staleTime: 1000 * 5,
      cacheTime: 1000 * 5,
      select: createNativeModel,
      onError: options?.onError,
    }
  );

  return {
    adModel,
    refetch,
    data: adModel,
    isLoading,
    error,
    isSuccess,
    isVisible: adModel?.isVisible || false,
  };
};

// SSR을 위한 prefetch 함수
const prefetchAdControlNative = async (
  queryClient: QueryClient,
  adPageType: AdPageType,
  client: Client,
  contextId?: string
) => {
  const params = new AdropParamsFactory(adPageType, client);

  const unitId = params.getUnitId();
  if (!unitId) {
    return;
  }

  const props: AdControlRequest = {
    unitId: unitId,
    platform: params.getPlatform(),
    locale: 'ko_KR',
    trackMode: 1,
    contextId: contextId,
  };

  await queryClient.prefetchQuery(
    getKey(props),
    async () => {
      const response = await adcontrolQueryRequest({ ...props, adType: 'native' as const });
      return response.data as AdControlNativeResponse;
    },
    {
      staleTime: 1000 * 5,
      cacheTime: 1000 * 5,
    }
  );
};

useAdControlNative.prefetchQuery = prefetchAdControlNative;
useAdControlNative.getKey = getKey;

export default useAdControlNative;
